import { useId } from 'react';
import {
    faAlignLeft,
    faArrowUpWideShort,
    faLayerGroup,
    faLink,
    faRoute,
    faPlane,
} from '@fortawesome/free-solid-svg-icons';
import { darkenColor } from './utils/colors';

const getStatusColors = (status, backgroundColor) => {
    const darkerColor = backgroundColor ? darkenColor(backgroundColor, 25) : '#666666';
    const darkerStroke = darkenColor(darkerColor, 15);

    if (status === 'finished') {
        return {
            fill: darkerColor,
            stroke: darkerStroke,
            overlayFill: null,
        };
    }

    if (status === 'in_progress') {
        return {
            fill: '#fff',
            stroke: darkerColor,
            overlayFill: darkerColor,
        };
    }

    return {
        fill: '#fff',
        stroke: '#000',
        overlayFill: null,
    };
};

const StatusFaPathIcon = ({
    icon,
    size = 48,
    status,
    backgroundColor,
    renderMode,
    transform,
    rotateDeg,
}) => {
    const clipId = useId().replace(/:/g, '');
    const [width, height, , , pathData] = icon.icon;
    const path = Array.isArray(pathData) ? pathData[0] : pathData;
    const isSolidMode = renderMode === 'solid';
    const { fill, stroke, overlayFill } = isSolidMode
        ? { fill: 'currentColor', stroke: 'none', overlayFill: null }
        : getStatusColors(status, backgroundColor);

    const commonPathProps = {
        d: path,
        ...(isSolidMode ? {} : {
            vectorEffect: 'non-scaling-stroke',
            strokeWidth: 1,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
            paintOrder: 'stroke fill',
        }),
    };
    const combinedTransform = [
        transform,
        typeof rotateDeg === 'number' ? `rotate(${rotateDeg} ${width / 2} ${height / 2})` : null,
    ].filter(Boolean).join(' ') || undefined;

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${width} ${height}`}
            style={{ display: 'block', overflow: 'visible' }}
            aria-hidden="true"
            focusable="false"
        >
            {overlayFill ? (
                <defs>
                    <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                        <rect x="0" y={height / 2} width={width} height={height / 2} />
                    </clipPath>
                </defs>
            ) : null}
            <g transform={combinedTransform}>
                <path {...commonPathProps} fill={fill} stroke={stroke} />
                {overlayFill ? (
                    <path
                        d={path}
                        fill={overlayFill}
                        clipPath={`url(#${clipId})`}
                    />
                ) : null}
            </g>
        </svg>
    );
};

// --- CollegamentiIcon Component ---
export const CollegamentiIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faLink}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
        />
    );
};
/*
export const CollegamentiIcon = ({ size = 48 }) => {
  // 4x4 grid, all white except 4 random squares are light purple
  const lightPurple = '#e6d6f3';
  const white = '#fff';
  // Use a fixed seed for stable randomness
  const getRandomIndices = () => {
    // Simple deterministic shuffle
    const arr = Array.from({ length: 16 }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = (i * 7 + 3) % (i + 1); // pseudo-random but stable
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 4);
  };
  const purpleIndices = getRandomIndices();
  return (
    <div
      className="collegamenti-icon"
      style={{ width: size, height: size, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(4, 1fr)', gap: 1 }}
    >
      {Array.from({ length: 16 }).map((_, i) => (
        <div
          key={i}
          style={{
            background: purpleIndices.includes(i) ? lightPurple : white,
            borderRadius: 3,
            width: '100%',
            height: '100%',
            border: '1px solid #b76dc5',
          }}
        />
      ))}
    </div>
  );
};
*/

/* --- OrtografiaIcon Component --- */
export const OrtografiaMenuIcon = ({ size = 48 }) => {
    // Menu version - 2-3-2 hexagon hive layout, uses currentColor to inherit text color
    const hexRadius = size / 5.2;
    const hexHeight = Math.sqrt(3) * hexRadius;
    const hexWidth = 2 * hexRadius;
    const vSpacing = hexHeight * 0.88;
    const hSpacing = hexWidth * 0.88;

    const centerX = size / 2;
    const centerY = size / 2;
    const totalHeight = vSpacing * 2 + hexHeight;
    const yOffset = centerY - totalHeight / 2 + hexHeight / 2;

    const centers = [
        { x: centerX - hSpacing / 2, y: yOffset },
        { x: centerX + hSpacing / 2, y: yOffset },
        { x: centerX - hSpacing, y: yOffset + vSpacing },
        { x: centerX, y: yOffset + vSpacing },
        { x: centerX + hSpacing, y: yOffset + vSpacing },
        { x: centerX - hSpacing / 2, y: yOffset + 2 * vSpacing },
        { x: centerX + hSpacing / 2, y: yOffset + 2 * vSpacing },
    ];

    const getHexPoints = (cx, cy, r) =>
        Array.from({ length: 6 }).map((_, i) => {
            const angle = Math.PI / 3 * i - Math.PI / 6;
            return [
                cx + r * Math.cos(angle),
                cy + r * Math.sin(angle)
            ];
        }).map(([x, y]) => `${x},${y}`).join(' ');

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
            {centers.map((c, i) => (
                <polygon
                    key={i}
                    points={getHexPoints(c.x, c.y, hexRadius)}
                    fill="currentColor"
                />
            ))}
        </svg>
    );
};

export const OrtografiaIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    const isSolidMode = renderMode === 'solid';
    // 2-3-2 hexagon hive layout, regular hexagons, fit to square
    const strokeWidth = 1;
    const hexRadius = size / 5.2;
    const hexHeight = Math.sqrt(3) * hexRadius;
    const hexWidth = 2 * hexRadius;
    const vSpacing = hexHeight * 0.88;
    const hSpacing = hexWidth * 0.88;

    // Center the hive in the square
    const centerX = size / 2;
    const centerY = size / 2;
    // Calculate vertical offset to center the hive
    const totalHeight = vSpacing * 2 + hexHeight;
    const yOffset = centerY - totalHeight / 2 + hexHeight / 2;

    // Centers for 2-3-2 layout
    const centers = [
        // Top row (2)
        { x: centerX - hSpacing / 2, y: yOffset },
        { x: centerX + hSpacing / 2, y: yOffset },
        // Middle row (3)
        { x: centerX - hSpacing, y: yOffset + vSpacing },
        { x: centerX, y: yOffset + vSpacing },
        { x: centerX + hSpacing, y: yOffset + vSpacing },
        // Bottom row (2)
        { x: centerX - hSpacing / 2, y: yOffset + 2 * vSpacing },
        { x: centerX + hSpacing / 2, y: yOffset + 2 * vSpacing },
    ];

    // Helper to get hexagon points
    const getHexPoints = (cx, cy, r) =>
        Array.from({ length: 6 }).map((_, i) => {
            const angle = Math.PI / 3 * i - Math.PI / 6;
            return [
                cx + r * Math.cos(angle),
                cy + r * Math.sin(angle)
            ];
        }).map(([x, y]) => `${x},${y}`).join(' ');

    // Determine colors based on status
    const darkerColor = backgroundColor ? darkenColor(backgroundColor, 25) : '#666';
    const darkerStroke = backgroundColor ? darkenColor(backgroundColor, 40) : '#333';
    
    let fillColor = isSolidMode ? 'currentColor' : '#fff';
    let strokeColor = isSolidMode ? 'none' : '#000';
    
    if (!isSolidMode && status === 'in_progress') {
        // For in_progress, we'll fill bottom hexagons with color
        // Centers are ordered: top row (0,1), middle row (2,3,4), bottom row (5,6)
        // Fill bottom 4 hexagons (indices 3,4,5,6) to show ~50% progress
    } else if (!isSolidMode && status === 'finished') {
        fillColor = darkerColor;
        strokeColor = darkerStroke;
    }

    return (
        <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
                {centers.map((c, i) => {
                    // For in_progress: fill bottom hexagons (indices 3,4,5,6)
                    let hexFill = fillColor;
                    let hexStroke = strokeColor;
                    
                    if (!isSolidMode && status === 'in_progress') {
                        if (i >= 3) {
                            hexFill = darkerColor;
                            hexStroke = darkerStroke;
                        } else {
                            hexFill = '#fff';
                            hexStroke = '#000';
                        }
                    }
                    
                    return (
                        <polygon
                            key={i}
                            points={getHexPoints(c.x, c.y, isSolidMode ? hexRadius : hexRadius - strokeWidth / 2)}
                            fill={hexFill}
                            stroke={hexStroke}
                            strokeWidth={isSolidMode ? 0 : strokeWidth}
                        />
                    );
                })}
            </svg>
        </div>
    );
};

// --- ScalinataIcon Component ---
export const ScalinataIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faLayerGroup}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
        />
    );
};
/*
export const ScalinataIcon = ({ size = 48 }) => {
  // Colors from Scalinata.css
  const white = '#fff';
  const gray = '#bdbdbd';
  const orange = '#f7b267';
  const green = '#7cb342';
  // Each row: 1, 2, 3, 4 blocks, left-aligned
  const blockColors = [
    [green],
    [orange, gray],
    [white, green, orange],
    [gray, white, green, orange],
  ];
  const maxBlocks = 4;
  const blockSize = size / (maxBlocks + 0.5); // fit 4 blocks with some gap
  const gap = blockSize * 0.18;
  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      {blockColors.map((row, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginBottom: rowIdx < blockColors.length - 1 ? gap : 0,
          }}
        >
          {row.map((color, colIdx) => (
            <div
              key={colIdx}
              style={{
                width: blockSize,
                height: blockSize,
                background: color,
                borderRadius: 3,
                border: `1.5px solid ${gray}`,
                marginRight: colIdx < row.length - 1 ? gap : 0,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
*/

// --- MemoriaIcon Component ---
export const MemoriaIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faPlane}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
            rotateDeg={-45}
        />
    );
};
/*
export const MemoriaIcon = ({ size = 48 }) => {
  // 2x2 grid of cards, two face up (fa-plane), two face down
  const cardSize = size / 2.3;
  const gap = cardSize * 0.18;
  const cardBack = '#f7e6e6';
  const cardBorder = '#e57373';
  const cardFront = '#fff';
  // Which cards are face up (showing plane)
  const faceUp = [true, false, true, false];
  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: gap,
        alignItems: 'center',
        justifyItems: 'center',
      }}
    >
      {faceUp.map((isUp, idx) => (
        <div
          key={idx}
          style={{
            width: cardSize,
            height: cardSize,
            background: isUp ? cardFront : cardBack,
            border: `2px solid ${cardBorder}`,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: cardSize * 0.7,
            color: '#e57373',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}
        >
          {isUp ? <i className="fa fa-plane" aria-label="plane" /> : null}
        </div>
      ))}
    </div>
  );
};
*/

// --- ClozeIcon Component ---
export const ClozeIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faAlignLeft}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
        />
    );
};

// --- SpettriIcon Component ---
export const SpettriIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faArrowUpWideShort}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
        />
    );
};

// --- SerpentoniIcon Component ---
export const SerpentoniIcon = ({ size = 48, status, backgroundColor, renderMode }) => {
    return (
        <StatusFaPathIcon
            icon={faRoute}
            size={size}
            status={status}
            backgroundColor={backgroundColor}
            renderMode={renderMode}
        />
    );
};

// --- GettonciniIcon Component ---
export const GettonciniIcon = ({ size = 24, className = '' }) => {
    return (
        <svg 
            className={className} 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
        >
            <defs>
                <clipPath id="gettoncini-clip">
                    <circle cx="12" cy="12" r="10"/>
                </clipPath>
            </defs>
            <circle cx="12" cy="12" r="10" fill="#2563eb" stroke="#1e40af" strokeWidth="1.5"/>
            <g clipPath="url(#gettoncini-clip)">
                <circle cx="12" cy="12" r="7" fill="#3b82f6" opacity="0.3"/>
                <text 
                    x="12" 
                    y="10.5" 
                    fontSize="25" 
                    fill="#c0dcfc" 
                    fontFamily="'Corinthia', cursive"
                    fontWeight="700"
                    textAnchor="middle" 
                    dominantBaseline="central"
                >
                    g
                </text>
            </g>
        </svg>
    );
};

// --- GettonciniMenuIcon Component (uses currentColor to inherit text color) ---
export const GettonciniMenuIcon = ({ size = 24 }) => {
    const scaledSize = size * 1.2; // 20% larger
    return (
        <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            width={scaledSize}
            height={scaledSize}
            style={{ display: 'block' }}
        >
            {/* Outer circle - stroke only */}
            <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="2"/>
            {/* Inner circle decoration */}
            <circle cx="12" cy="12" r="6.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4"/>
            {/* The "g" letter */}
            <text 
                x="12" 
                y="10.5" 
                fontSize="18" 
                fill="currentColor" 
                fontFamily="'Corinthia', cursive"
                fontWeight="700"
                textAnchor="middle" 
                dominantBaseline="central"
            >
                g
            </text>
        </svg>
    );
};
